import { HttpClient } from '@angular/common/http';
import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { Cookie } from '@utilities/cookie';
import { Storage } from '@utilities/storage';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SynchronizationService } from './synchronization.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private static session: any = {};

  public get server(): string {
    return window.location.protocol + '//ninh.pos247.app/';
  }

  public credentialChanged: EventEmitter<RmsUserRecord> = new EventEmitter<RmsUserRecord>();

  constructor(private http: HttpClient, private router: Router, private sync: SynchronizationService) {
  }

  public login(username: string, password: string): Observable<boolean> {

    return this.rms<{ Username: string, Password: string }, { Result: number, CredentialID: string, SubjectID: number }>('RMS:Login', { Username: username, Password: password }).pipe(map(response => {
      let result = response != null && response.Result == RmsRequestResult.OK && response.Data.Result == 1;

      if (result) {
        Cookie.set('a2Token', response.Data.CredentialID);
        Storage.set<RmsUserRecord>('userdata', { Username: username, ID: response.Data.SubjectID });
        this.credentialChanged.emit({ Username: username, ID: response.Data.SubjectID });
      }

      return result;
    }));
  }


  public logout() {

    if (this.token) {
      Cookie.set<string>('a2Token', null);
      Storage.set<RmsUserRecord>('userdata', null);
      this.credentialChanged.emit(null);
      location.reload();
    }
  }

  public get token() {
    return Cookie.get<string>('a2Token', null);
  }

  public get user(): RmsUserRecord {
    return Storage.get<RmsUserRecord>('userdata', null);
  }

  public get authorized(): boolean {
    return this.token !== null;
  }

  public rms<TRequest, TResponse>(service: string, data: TRequest, reusable?: (data: TResponse) => boolean): Observable<RmsResponse<TResponse>> {

    this.sync.notify(ApiService, false);

    let request: RmsRequest<TRequest> = {
      CredentialID: this.token,
      CredentialType: 3,
      Data: data
    };

    return new Observable<RmsResponse<TResponse>>(o => {

      let key = service + "/" + JSON.stringify(data);

      if (reusable && ApiService.session[key] && reusable(ApiService.session[key])) {

        o.next(ApiService.session[key]);
        o.complete();
        this.sync.notify(ApiService, true);
        return;
      }

      this.http.post<RmsResponse<TResponse>>(this.server + service, request).subscribe(

        response => {

          if (reusable && response.Result == RmsRequestResult.OK && reusable(response.Data)) {
            ApiService.session[key] = response;
          }

          o.next(response);
          o.complete();
          this.sync.notify(ApiService, true);

          if (response.Result === RmsRequestResult.InvalidCredential) {

            this.logout();
          }
        },

        error => {
          o.next({ Result: RmsRequestResult.Unknown, Data: null });
          o.complete();
          this.sync.notify(ApiService, true);
        }
      );
    });
  }
}

export interface RmsRequest<T> {
  CredentialID: string;
  CredentialType: number;
  Data: T;
}

export interface RmsResponse<T> {
  Result: RmsRequestResult;
  Data: T;
}

export enum RmsRequestResult {
  Unknown = 0,
  NotPermitted = 1,
  InvalidCredential = 2,
  InvalidRequest = 3,
  OK = 4
}

export interface RmsUserRecord {
  ID: number;
  Username: string;
}

export class RmsApiRequest<TRequest, TResponse> {

  private _provider: ApiService;
  private _service: string;
  private _request: TRequest;

  constructor(provider: ApiService, service: string, request: TRequest) {
    this._provider = provider;
    this._service = service;
    this._request = request;
  }

  private _response: RmsResponse<TResponse> = null;
  private _observer: Observable<RmsApiRequest<TRequest, TResponse>> = null;

  public get result(): RmsRequestResult {
    return this._response == null ? RmsRequestResult.Unknown : this._response.Result;
  }

  public get data(): TResponse {
    return this._response == null ? null : this._response.Data;
  }

  public get completed(): boolean {
    return this._response != null;
  }

  public get succeed() {
    return this.result === RmsRequestResult.OK;
  }

  public get failed() {
    return this.result !== RmsRequestResult.OK;
  }

  public get provider() {
    return this._provider;
  }

  public submit(api: ApiService, reusable: (data:TResponse) => boolean): Observable<RmsApiRequest<TRequest, TResponse>> {

    if (this._observer == null) {

      this._observer = new Observable<RmsApiRequest<TRequest, TResponse>>(o => {

        api.rms<TRequest, TResponse>(this._service, this._request, reusable).subscribe(response => {

          this._response = response;

          o.next(this);
          o.complete();

        });
      });
    }

    return this._observer;
  }
}
